import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, Upload, Eye, Edit, Trash2, User, Award, Building, Target, Clock } from 'lucide-react';
import { useStudents } from './hooks/useStudents';
import { useSchools } from './hooks/useSchools';
import { useDebounce } from '../../hooks/useDebounce';
import type { SchoolStudent, SchoolStudentCreateRequest } from './types';
import CreateSchoolStudentModal from './components/CreateSchoolStudentModal';
import ImportSchoolStudentModal from './components/ImportSchoolStudentModal';
import SchoolStudentDetail from './components/SchoolStudentDetail';
import { schoolService } from '../../services';
import { buildStudentUpdatePayload } from './utils/buildStudentUpdatePayload';
import AdminListSortSelect from '../../components/ui/AdminListSortSelect';
import { getApiErrorMessage } from '../../lib/api-error';

// API không cho xóa các field này (sometimes|required)
const NON_CLEARABLE_FIELDS = {
  email: 'email',
  phone: 'số điện thoại',
  code: 'mã học sinh',
  grade: 'khối',
  schoolYear: 'năm học',
} as const;

const normalizeSchoolStudent = (student: any): SchoolStudent => {
  const metadata = student.metadata || {};

  return {
    id: student.id || student._id,
    name: student.user?.name || student.name || '',
    email: student.user?.email || student.email || '',
    phone: student.user?.phone || student.phone || '',
    code: student.user?.code || student.code || '',
    schoolId: student.schoolId || student.school?.id || '',
    schoolName: student.school?.name || student.schoolName || '',
    status: student.status,
    studentId: metadata.studentId,
    dateOfBirth: metadata.dateOfBirth,
    gender: metadata.gender,
    address: metadata.address,
    parentName: metadata.parentName,
    parentPhone: metadata.parentPhone,
    parentEmail: metadata.parentEmail,
    emergencyContact: metadata.emergencyContact,
    emergencyPhone: metadata.emergencyPhone,
    grade: metadata.grade || student.class?.grade,
    classId: student.classId || student.class?.id || metadata.classId,
    className: student.class?.name,
    schoolYear: metadata.schoolYear || student.class?.schoolYear,
    enrollmentDate: metadata.enrollmentDate,
    graduationDate: metadata.graduationDate,
    academicLevel: metadata.academicLevel,
    gpa: metadata.gpa,
    attendance: metadata.attendance,
    subjects: metadata.subjects || [],
    metadata,
    createdAt: student.createdAt || student.created_at || '',
    updatedAt: student.updatedAt || student.updated_at || '',
  };
};

const SchoolStudentDashboard: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [viewingStudent, setViewingStudent] = useState<SchoolStudent | null>(null);
  const [editingStudent, setEditingStudent] = useState<SchoolStudent | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  
  const {
    students,
    pagination,
    filters,
    isLoading,
    error,
    updateFilters,
    updatePagination,
    resetFilters,
    refetch,
    deleteStudent,
    toggleStudentStatus,
    isDeleting,
    isTogglingStatus,
    createStudent,
    isCreating,
    isUpdating,
    createStudentMutation,
    updateStudentMutation,
    deleteStudentMutation,
    toggleStatusMutation
  } = useStudents();

  const { schoolsResponse, isLoadingSchools } = useSchools();
  const allSchools = schoolsResponse?.data || [];

  useEffect(() => {
    updateFilters({ search: debouncedSearch });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
  }, [pagination.page, pagination.per_page, filters]);

  const handleFilterChange = (key: keyof any, value: string) => {
    updateFilters({ [key]: value });
  };

  const handlePageChange = (page: number) => {
    updatePagination(page);
  };

  const handlePerPageChange = (perPage: number) => {
    updatePagination(1, perPage);
  };

  const handleImportStudents = () => {
    setIsImportModalOpen(true);
  };

  const handleCreateStudentSubmit = async (data: SchoolStudentCreateRequest) => {
    try {
      await createStudentMutation.mutateAsync(data);
      setIsCreateModalOpen(false);
      toast.success(`Tạo học sinh "${data.name}" thành công!`);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Có lỗi xảy ra khi tạo học sinh'));
      throw error; // Re-throw to let modal handle it
    }
  };

  // Ref chứ không phải state: hai cú click trong cùng một tick vẫn thấy cờ ngay
  const isLoadingStudentRef = useRef(false);
  const [loadingStudentId, setLoadingStudentId] = useState<string | null>(null);

  const openStudent = async (studentId: string, mode: 'view' | 'edit') => {
    if (isLoadingStudentRef.current) return;
    isLoadingStudentRef.current = true;
    setLoadingStudentId(studentId);

    try {
      const student = normalizeSchoolStudent(await schoolService.getSchoolStudent(studentId));
      if (mode === 'view') {
        setViewingStudent(student);
      } else {
        setEditingStudent(student);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tải thông tin học sinh');
    } finally {
      isLoadingStudentRef.current = false;
      setLoadingStudentId(null);
    }
  };

  const handleViewStudent = (studentId: string) => openStudent(studentId, 'view');
  const handleEditStudent = (studentId: string) => openStudent(studentId, 'edit');

  const handleUpdateStudentSubmit = async (data: SchoolStudentCreateRequest) => {
    if (!editingStudent) return;

    const updateData = buildStudentUpdatePayload(editingStudent, data);
    const clearedField = (Object.keys(NON_CLEARABLE_FIELDS) as Array<keyof typeof NON_CLEARABLE_FIELDS>)
      .find((field) => field in updateData && !updateData[field]);
    if (clearedField) {
      toast.error(`Không thể xóa ${NON_CLEARABLE_FIELDS[clearedField]} của học sinh. Hãy nhập giá trị mới.`);
      return;
    }
    if (Object.keys(updateData).length === 0) {
      setEditingStudent(null);
      toast('Không có thay đổi nào để lưu');
      return;
    }

    try {
      await updateStudentMutation.mutateAsync({ id: editingStudent.id, data: updateData });
      setEditingStudent(null);
      toast.success(`Cập nhật học sinh "${data.name}" thành công!`);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Có lỗi xảy ra khi cập nhật học sinh'));
    }
  };

  const handleDeleteStudent = (studentId: string, studentName: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">⚠️</span>
          <span className="font-medium">Xác nhận xóa học sinh</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn xóa học sinh <strong>"{studentName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                deleteStudentMutation.mutateAsync(studentId),
                {
                  loading: 'Đang xóa học sinh...',
                  success: `Xóa học sinh "${studentName}" thành công!`,
                  error: (err) => err?.response?.data?.message || 'Có lỗi xảy ra khi xóa học sinh',
                }
              );
            }}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Xóa
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleToggleStatus = (studentId: string, studentName: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const actionText = currentStatus === 'active' ? 'tạm dừng' : 'kích hoạt';
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{currentStatus === 'active' ? '⏸️' : '▶️'}</span>
          <span className="font-medium">Xác nhận {actionText} học sinh</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn {actionText} học sinh <strong>"{studentName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                toggleStatusMutation.mutateAsync({ id: studentId, status: newStatus }),
                {
                  loading: `Đang ${actionText} học sinh...`,
                  success: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} học sinh "${studentName}" thành công!`,
                  error: (err) => err?.response?.data?.message || `Có lỗi xảy ra khi ${actionText} học sinh`,
                }
              );
            }}
            className={`px-3 py-1 text-sm text-white rounded transition-colors ${
              currentStatus === 'active' 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleImportSuccess = () => {
    refetch();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      graduated: 'bg-blue-100 text-blue-800',
      transferred: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === 'active' && 'Đang học'}
        {status === 'inactive' && 'Nghỉ học'}
        {status === 'graduated' && 'Đã tốt nghiệp'}
        {status === 'transferred' && 'Chuyển trường'}
      </span>
    );
  };

  const getGenderBadge = (gender: string) => {
    const genderConfig: Record<string, string> = {
      male: 'bg-blue-100 text-blue-800',
      female: 'bg-pink-100 text-pink-800',
      other: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${genderConfig[gender] || 'bg-gray-100 text-gray-800'}`}>
        {gender === 'male' && 'Nam'}
        {gender === 'female' && 'Nữ'}
        {gender === 'other' && 'Khác'}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center mb-6">
          <div className="space-y-2">
            <div className="h-8 w-80 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-96 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
              <div className="flex gap-3">
                <div className="h-10 w-48 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <th key={i} className="px-6 py-3 text-left">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                        <div className="ml-4 space-y-2">
                          <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          <div className="h-3 w-24 bg-gray-200 rounded"></div>
                          <div className="h-3 w-20 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-16 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-20 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Skeleton */}
        <div className="mt-6 flex justify-between items-center">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div className="flex space-x-2">
            <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 mb-2">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Lỗi tải dữ liệu</h3>
          <p className="text-red-600 mb-4">Không thể tải danh sách học sinh. Vui lòng thử lại sau.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Học sinh</h1>
          <p className="text-gray-600 mt-1">
            Quản lý danh sách học sinh, thông tin và kết quả học tập
          </p>
        </div>
        <div className="flex space-x-3">
            <button
            onClick={handleImportStudents}
            disabled={isCreating}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import từ Excel
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              </div>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, số điện thoại..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              {(searchInput !== debouncedSearch || (debouncedSearch && isLoading)) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <select 
              value={filters.schoolId || 'all'} 
              onChange={(e) => {
                const value = e.target.value === 'all' ? '' : e.target.value;
                console.log('School select changed:', { value, isAll: e.target.value === 'all' });
                handleFilterChange('schoolId', value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingSchools}
            >
              <option value="all">
                {isLoadingSchools ? 'Đang tải trường học...' : 'Tất cả trường học'}
              </option>
              {allSchools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
            <AdminListSortSelect
              value={filters.sort}
              onChange={(value) => handleFilterChange('sort', value)}
              disabled={isLoading}
            />
            <button
              onClick={() => {
                resetFilters();
                toast.success('Đã reset bộ lọc', {
                  icon: '🔄',
                  duration: 2000,
                });
              }}
              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reset
            </button>
          </div>
        </div>
      </div>


      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div ref={tableScrollRef} className="overflow-auto max-h-[max(420px,calc(100vh-360px))]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10 [&_th]:shadow-[0_1px_0_0_#e5e7eb]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  HỌC SINH
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TRƯỜNG HỌC
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LỚP HỌC
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GIỚI TÍNH
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TRẠNG THÁI
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  HÀNH ĐỘNG
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <User className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">Không có học sinh nào</p>
                      <p className="text-sm">Hãy thêm học sinh đầu tiên để bắt đầu</p>
                    </div>
                  </td>
                </tr>
              ) : (
                students.map((student: any) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <button
                            type="button"
                            onClick={() => handleViewStudent(student.id)}
                            disabled={!!loadingStudentId}
                            className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline disabled:cursor-wait"
                          >
                            {student.user?.name || 'N/A'}
                          </button>
                          {student.user?.email && (
                            <div className="text-sm text-gray-500">{student.user.email}</div>
                          )}
                          {student.user?.code && (
                            <div className="text-xs text-gray-400">{student.user.code}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.school?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{student.school?.code || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.class?.name || '-'}</div>
                      <div className="text-xs text-gray-500">
                        {student.class ? `${student.class.grade} - ${student.class.schoolYear}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getGenderBadge(student.metadata?.gender || '')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(student.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => handleViewStudent(student.id)}
                          disabled={!!loadingStudentId}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-wait"
                          title="Xem chi tiết"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEditStudent(student.id)}
                          disabled={!!loadingStudentId}
                          className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-wait"
                          title="Chỉnh sửa"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(student.id, student.user?.name || 'N/A', student.status)}
                          disabled={isTogglingStatus}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                            student.status === 'active' 
                              ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                          title={student.status === 'active' ? 'Tạm dừng hoạt động' : 'Kích hoạt hoạt động'}
                        >
                          {student.status === 'active' ? (
                            <Clock className="h-5 w-5" />
                          ) : (
                            <Award className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student.id, student.user?.name || 'N/A')}
                          disabled={isDeleting}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Xóa học sinh"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700 mr-4">
                  Hiển thị {((pagination.page - 1) * pagination.per_page) + 1} đến {Math.min(pagination.page * pagination.per_page, pagination.total)} trong tổng số {pagination.total} học sinh
                </span>
                <div className="flex items-center">
                  <span className="text-sm text-gray-700 mr-2">Hiển thị:</span>
                  <select
                    value={pagination.per_page}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Trước
                </button>

                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        pagination.page === pageNum
                          ? 'bg-green-600 text-white border-green-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                  <>
                    <span className="px-2 text-gray-500">...</span>
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    >
                      {pagination.totalPages}
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Sau
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateSchoolStudentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateStudentSubmit}
        isLoading={isCreating}
      />

      <CreateSchoolStudentModal
        isOpen={!!editingStudent}
        onClose={() => setEditingStudent(null)}
        editingStudent={editingStudent}
        onSave={handleUpdateStudentSubmit}
        isLoading={isUpdating}
      />

      {viewingStudent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setViewingStudent(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <SchoolStudentDetail student={viewingStudent} onClose={() => setViewingStudent(null)} />
          </div>
        </div>
      )}

        <ImportSchoolStudentModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={handleImportSuccess}
        />
    </div>
  );
};

export default SchoolStudentDashboard;
